import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Exploring NextJS with a custom CMS, pt3 – CodeWithSwiz",
  "description": "Friend, this episode of #CodeWithSwiz was a great example of why you want to start using TypeScript and how thermal load *destroys* computers.",
  "published": "2020-09-08T00:00:00.000Z",
  "categories": "CodeWithSwiz, Livecoding, Technical",
  "hero": "./img/screenshot-1599576984304.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Friend, this episode of #CodeWithSwiz is a great example of why you want to start using TypeScript and how thermal load `}<em parentName="p">{`destroys`}</em>{` computers.`}</p>
    <p><em parentName="p">{`CodeWithSwiz is a twice-a-week live show. Like a podcast with video and fun hacking. Focused on experiments. `}<a parentName="em" {...{
          "href": "https://youtube.com/swizecteller"
        }}>{`Join live Wednesdays and Sundays`}</a></em></p>
    <lite-youtube {...{
      "videoid": "yG2B1jV8o5g",
      "videostartat": "0"
    }}></lite-youtube>
    <h2 {...{
      "id": "thermal-load-is-fun-for-computers"
    }}>{`Thermal load is fun for computers`}</h2>
    <p>{`First, the thermals 👉 it was a hot day in San Francisco and this city doesn't know about AC or insulation. Temperate climate with a few hot days, why bother.`}</p>
    <p>{`Computers don't like that.`}</p>
    <p>{`Your computer uses `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/MOSFET"
      }}>{`MOSFET transistors`}</a>{` to perform computation. The details of how that works took my college physics professor a whole semester to explain and I'm not gonna try. The `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/MOSFET"
      }}>{`wikipedia article`}</a>{` is great ✌️`}</p>
    <p>{`Heat makes those transistors unreliable. 1 becomes 0, 0 becomes 1, and you get system crashes, hanged CPUs, or incorrect results without knowing they're incorrect.`}</p>
    <p>{`This is why my `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/i-built-a-node-app-to-thaw-my-favorite-snack/"
      }}>{`Node app to thaw carrots with my laptop`}</a>{` was a bad idea. Fun but bad 🥕`}</p>
    <p>{`The faster your computer runs, the more heat it produces, the less reliable it becomes. That's why you don't have a 6GHz laptop. You'd need a cooler the size of your fridge. `}<a parentName="p" {...{
        "href": "https://www.vice.com/en_us/article/bv8a58/doom-eternal-runs-at-1000-fps-if-youve-got-liquid-nitrogen"
      }}>{`Or a dash of liquid nitrogen to run DOOM at 1000fps`}</a></p>
    <p>{`Fun fact: `}<a parentName="p" {...{
        "href": "https://www.fastcompany.com/90219684/this-town-will-get-its-heat-from-an-unlikely-source-a-data-center"
      }}>{`Norway uses the heat output of data centers to heat entire towns through winter`}</a>{`.`}</p>
    <p>{`In lieu of a gigantic fridge, your laptop downclocks itself when it gets warm. Better to be reliable and slow than fast, unreliable, and broken.`}</p>
    <p>{`And you can see that in my video above. Encoding got overwhelmed, frames started dropping, audio was good.`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media4.giphy.com/media/3ohzdYJK1wAdPWVk88/giphy-loop.mp4?cid=7cf28b0fctj6blo17n0u2j9mabnhkum43v4mh8xoq2mx647n&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <h2 {...{
      "id": "why-you-should-consider-typescript"
    }}>{`Why you should consider TypeScript`}</h2>
    <p>{`This entire episode was about fixing a bug.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/58ad3998b201ae4734f0a1c2bf4435b1/3dfa7/NextJS-shows-a-cryptic-error4deg87.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "38.56502242152467%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'154\\'%20viewBox=\\'0%200%20400%20154\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M18%2086v19l1%2018h365V85H201L18%2086\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/58ad3998b201ae4734f0a1c2bf4435b1/ca0a1/NextJS-shows-a-cryptic-error4deg87.webp 223w", "/static/58ad3998b201ae4734f0a1c2bf4435b1/75680/NextJS-shows-a-cryptic-error4deg87.webp 445w", "/static/58ad3998b201ae4734f0a1c2bf4435b1/8d1ba/NextJS-shows-a-cryptic-error4deg87.webp 890w", "/static/58ad3998b201ae4734f0a1c2bf4435b1/3838e/NextJS-shows-a-cryptic-error4deg87.webp 1335w", "/static/58ad3998b201ae4734f0a1c2bf4435b1/e11e5/NextJS-shows-a-cryptic-error4deg87.webp 1780w", "/static/58ad3998b201ae4734f0a1c2bf4435b1/bf558/NextJS-shows-a-cryptic-error4deg87.webp 2028w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/58ad3998b201ae4734f0a1c2bf4435b1/e92b6/NextJS-shows-a-cryptic-error4deg87.png 223w", "/static/58ad3998b201ae4734f0a1c2bf4435b1/e66bf/NextJS-shows-a-cryptic-error4deg87.png 445w", "/static/58ad3998b201ae4734f0a1c2bf4435b1/4ef49/NextJS-shows-a-cryptic-error4deg87.png 890w", "/static/58ad3998b201ae4734f0a1c2bf4435b1/4e814/NextJS-shows-a-cryptic-error4deg87.png 1335w", "/static/58ad3998b201ae4734f0a1c2bf4435b1/701e9/NextJS-shows-a-cryptic-error4deg87.png 1780w", "/static/58ad3998b201ae4734f0a1c2bf4435b1/3dfa7/NextJS-shows-a-cryptic-error4deg87.png 2028w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/58ad3998b201ae4734f0a1c2bf4435b1/4ef49/NextJS-shows-a-cryptic-error4deg87.png",
                "alt": "NextJS shows a cryptic error",
                "title": "NextJS shows a cryptic error",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`NextJS shows a cryptic error`}</figcaption>{`
  `}</figure></p>
    <p>{`You get a cryptic error from a library that you created. Your code builds without error. Try to use it and get a cryptic `}<em parentName="p">{`"X is not a constructor"`}</em>{` message.`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media0.giphy.com/media/ukGm72ZLZvYfS/giphy-loop.mp4?cid=7cf28b0f766lddtru63b46eowbdbg7xjynwhc6hfkj3znlhc&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p>{`At first I thought this was a `}<a parentName="p" {...{
        "href": "https://github.com/developit/microbundle"
      }}>{`microbundle`}</a>{` bug, then a `}<a parentName="p" {...{
        "href": "https://github.com/parcel-bundler/parcel"
      }}>{`parcel`}</a>{` bug. You can see that flipflop in `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/building-a-small-cms-with-nextjs-pt2-codewithswiz/"
      }}>{`the previous episode of CodeWithSwiz`}</a>{`.`}</p>
    <p>{`Removed images from source, changed to text, and went back to microbundle. Didn't help.`}</p>
    <p>{`After the stream I tried one last debugging trick 👉 look at the compiled source. Open `}<inlineCode parentName="p">{`dist/index.js`}</inlineCode>{` in VSCode, run prettier, see if anything stands out.`}</p>
    <p><img parentName="p" {...{
        "src": "/79316af63a0340be40cb8c1bef5e0e17/Prettifying-compiled-source-to-spot-bugs92dc09.gif",
        "alt": "Prettifying compiled source to spot bugs"
      }}></img></p>
    <p>{`Okay `}<inlineCode parentName="p">{`l`}</inlineCode>{` is the `}<inlineCode parentName="p">{`@octokit/rest`}</inlineCode>{` library. How am I using that? 🤔`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`import Octokit from "@octokit/rest";
`}</code></pre>
    <p>{`That looks fine ... what does `}<a parentName="p" {...{
        "href": "https://www.npmjs.com/package/@octokit/rest"
      }}>{`the readme say`}</a>{`?`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const { Octokit } = require("@octokit/rest");
`}</code></pre>
    <p>{`It's a named export, not a default export. 🤦‍♂️`}</p>
    <p>{`Change that line of code and the error goes away.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`import { Octokit } from "@octokit/rest";
`}</code></pre>
    <p>{`An hour wasted because JavaScript can't know you're using a library, function, or variable incorrectly until you run the code. TypeScript would yell at us and we'd fix the bug in 3 minutes.`}</p>
    <p>{`Lesson learned my friend. We continue on Sunday`}</p>
    <p>{`Cheers,`}<br />{`
~Swizec`}</p>
    <p>{`PS: we also ran into `}<a parentName="p" {...{
        "href": "https://github.com/developit/microbundle/issues/708"
      }}>{`a microbundle compilation bug`}</a>{` that means you have to add `}<inlineCode parentName="p">{`import 'regenerator-runtime/runtime'`}</inlineCode>{` to your code. Don't worry about it, there's a PR ✌️`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      